import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import {
  CardPaymentRedirectResponse,
  startLightstepSpan,
  WorldPayOrderStatus,
  WorldPayResponse,
} from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CompleteOrderBackendService } from '@complete-order/services/complete-order-backend.service';

declare const WPCL;

@Component({
  selector: 'app-worldpay',
  templateUrl: './worldpay.component.html',
  styleUrls: ['./worldpay.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WorldPayComponent implements OnInit, OnDestroy {
  customisation = {
    fonts: {
      family: ['Arial'],
    },
    color: this.getCssVar('text-colour'),
    buttons: {
      backgroundColor: this.getCssVar('btn-primary-background-color'),
      color: this.getCssVar('btn-primary-color'),
      border: {
        style: 'solid',
        color: this.getCssVar('btn-primary-border-color'),
        width: '5px', //this.getCssVar('btn-border-width') WL TODO: coming out as 1.5px for some reason which doesn't work with worldpay
        radius: this.getCssVar('btn-border-radius'),
      },
      hover: {
        backgroundColor: this.getCssVar('primary-interactive-color--hover'),
        border: {
          color: this.getCssVar('primary-interactive-color--hover'),
        },
      },
    },
    inputs: {
      backgroundColor: this.getCssVar('input-background-color'),
      border: {
        style: 'solid',
        width: this.getCssVar('input-border-width'),
        radius: this.getCssVar('input-border-radius'),
        color: this.getCssVar('input-border-color'),
      },
      placeholder: {
        color: this.getCssVar('text-color--placeholder'),
      },
      validation: {
        ok: {
          color: this.getCssVar('success'),
        },
        error: {
          color: this.getCssVar('error'),
        },
      },
    },
    page: {
      backgroundColor: this.getCssVar('page-bg'),
      color: this.getCssVar('text-colour'),
      border: {
        style: 'solid',
        color: this.getCssVar('input-border-color'),
        width: this.getCssVar('input-border-width'),
        radius: this.getCssVar('input-border-radius'),
      },
    },
  };

  testVersion = false;

  paymentProcessing = false;

  cachingOrder =
    this.completeOrderBackendService.attemptingToCacheOrder.asObservable();

  paymentAlreadyTaken =
    sessionStorage.getItem(
      `payment_taken_${this.route.snapshot.queryParams['PRRef']}`
    ) === 'true';

  @Input() set cardPaymentRedirect(response: CardPaymentRedirectResponse) {
    if (this.libraryObject) {
      this.libraryObject.destroy();
    }

    this.testVersion = response.url.includes('test');
    this.customOptions = {
      type: 'iframe',
      inject: 'immediate',
      iframeIntegrationId: 'libraryObject',
      flowCallback: this.flowCallbackFn,
      resultCallback: this.resultCallbackFn,
      iframeHelperURL: `${this.buildConfigService.config.domain}/worldpay/helper.html`,
      iframeBaseURL: `${this.buildConfigService.config.domain}`,
      url: `${response.url}`,
      target: 'paymentForm',
      accessibility: true,
      debug: false,
      language: 'en',
      country: 'gb',
      customisation: this.customisation,
    };

    this.libraryObject = new WPCL.Library();
    this.completeOrderService.loadWorldPay();

    setTimeout(() => {
      this.libraryObject.setup(this.customOptions); // push to end of stack
    });
  }

  constructor(
    private readonly buildConfigService: BuildConfigService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    public readonly completeOrderService: CompleteOrderService,
    public readonly cdr: ChangeDetectorRef,
    private readonly featureFlagService: FeatureFlagService,
    private readonly completeOrderBackendService: CompleteOrderBackendService
  ) {}

  ngOnInit() {
    this.completeOrderBackendService.isAtStageToPoll = true;
    if (this.featureFlagService.isFeatureFlagEnabled('newPaymentFlow')) {
      this.completeOrderService.cacheOrder();
    }
  }

  ngOnDestroy(): void {
    this.libraryObject?.destroy();
    this.completeOrderBackendService.isAtStageToPoll = false;
  }

  WorldPayOrderStatus = WorldPayOrderStatus;
  customOptions;
  libraryObject;
  page: string;
  result: WorldPayResponse;

  private bodyStyle: CSSStyleDeclaration;
  private getCssVar(varName: string) {
    if (!this.bodyStyle)
      this.bodyStyle = window.getComputedStyle(document.body);
    return this.bodyStyle.getPropertyValue(`--${varName}`).trim();
  }

  flowCallbackFn = (flow) => {
    this.page = flow.page;
    this.cdr.detectChanges();
  };

  resultCallbackFn = (result: WorldPayResponse) => {
    this.result = result;

    startLightstepSpan('pr.online.worldPayResultCallback', {
      status: result.order.status,
      error: result.error,
      reference: result.order.orderKey,
    }).end();

    if (this.result.order.status === WorldPayOrderStatus.Success) {
      this.paymentProcessing = true;
      // Used so we can detect if user tries to leave the page
      sessionStorage.setItem(`payment_taken_${result.order.orderKey}`, 'true');

      this.completeOrderService.loadWorldPaySuccess(result);
      this.router.navigate(['/', 'processing-order'], {
        queryParamsHandling: 'preserve',
      });

      this.completeOrderService.submitOrder();
    } else {
      this.completeOrderService.loadWorldPayFail(result);
      throw Error(JSON.stringify(result));
    }
    this.cdr.detectChanges();
  };

  refreshPage() {
    this.router.navigate(['/', 'processing-order'], {
      queryParamsHandling: 'preserve',
    });
  }
}
